import { FusionConfig, FusedRecord, MatchStatus } from '../../types/fusion';
import { AudioFeatureWindow } from '../../types/audio';
import { TrackingResult } from '../../types/tracking';

export class AcousticPositionFusionEngine {
  private config: FusionConfig = {
    matchToleranceMs: 150,
    primaryRhythm: 'audio',
  };

  private runId: string = '';
  private sequence = 0;
  private isStereoMode = false;

  // Recent history buffers for Camera A and Camera B
  private cameraABuffer: TrackingResult[] = [];
  private cameraBBuffer: TrackingResult[] = [];
  private maxBufferSize = 50; // Keep last ~50 tracking frames (~1.6s)

  private callbacks: Array<(record: FusedRecord) => void> = [];

  constructor(config?: Partial<FusionConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
  }

  configure(config: Partial<FusionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  setRunId(runId: string, isStereoMode: boolean): void {
    this.runId = runId;
    this.isStereoMode = isStereoMode;
    this.sequence = 0;
    this.cameraABuffer = [];
    this.cameraBBuffer = [];
  }

  pushCameraA(result: TrackingResult): void {
    this.cameraABuffer.push(result);
    if (this.cameraABuffer.length > this.maxBufferSize) {
      this.cameraABuffer.shift();
    }
  }

  pushCameraB(result: TrackingResult): void {
    this.cameraBBuffer.push(result);
    if (this.cameraBBuffer.length > this.maxBufferSize) {
      this.cameraBBuffer.shift();
    }
  }

  /**
   * Primary entry point: Audio feature window arrives, trigger fusion match
   */
  processAudioWindow(audioWindow: AudioFeatureWindow): FusedRecord {
    this.sequence++;
    const targetMs = audioWindow.monotonicMs;

    // Find nearest Camera A frame
    const matchA = this.findNearestTrackingResult(this.cameraABuffer, targetMs);
    // Find nearest Camera B frame if in stereo mode
    const matchB = this.isStereoMode ? this.findNearestTrackingResult(this.cameraBBuffer, targetMs) : null;

    const qualityFlags: string[] = [...audioWindow.qualityFlags];

    let camAResult: TrackingResult | undefined;
    let camATimeDiff: number | undefined;
    let isAMatched = false;

    if (matchA) {
      camATimeDiff = Math.round(Math.abs(targetMs - matchA.monotonicMs) * 10) / 10;
      if (camATimeDiff <= this.config.matchToleranceMs) {
        camAResult = matchA;
        isAMatched = true;
      } else {
        qualityFlags.push('camera-a-unmatched');
        camAResult = matchA; // Still provide for diagnostics, but flag unmatched
      }
    } else {
      qualityFlags.push('camera-a-missing');
    }

    let camBResult: TrackingResult | undefined;
    let camBTimeDiff: number | undefined;
    let isBMatched = false;

    if (this.isStereoMode) {
      if (matchB) {
        camBTimeDiff = Math.round(Math.abs(targetMs - matchB.monotonicMs) * 10) / 10;
        if (camBTimeDiff <= this.config.matchToleranceMs) {
          camBResult = matchB;
          isBMatched = true;
        } else {
          qualityFlags.push('camera-b-unmatched');
          camBResult = matchB;
        }
      } else {
        qualityFlags.push('camera-b-missing');
      }
    }

    let matchStatus: MatchStatus = 'unmatched';
    if (this.isStereoMode) {
      if (isAMatched && isBMatched) {
        matchStatus = 'matched';
      } else if (isAMatched || isBMatched) {
        matchStatus = 'partial';
      }
    } else {
      if (isAMatched) {
        matchStatus = 'matched';
      }
    }

    const fusedRecord: FusedRecord = {
      schemaVersion: '1.0.0',
      runId: this.runId || 'default-run',
      sequence: this.sequence,
      observedAt: audioWindow.observedAt,
      monotonicMs: audioWindow.monotonicMs,
      audioFeatures: audioWindow,
      cameraAPosition: camAResult,
      cameraATimeDiffMs: camATimeDiff,
      cameraBPosition: camBResult,
      cameraBTimeDiffMs: camBTimeDiff,
      matchStatus,
      coordinateSpace: this.isStereoMode ? 'stereo-2d-pair' : 'single-cam-2d',
      qualityFlags,
    };

    for (const cb of this.callbacks) {
      cb(fusedRecord);
    }

    return fusedRecord;
  }

  private findNearestTrackingResult(buffer: TrackingResult[], targetMs: number): TrackingResult | null {
    if (buffer.length === 0) return null;

    let closest: TrackingResult = buffer[0];
    let minDiff = Math.abs(targetMs - closest.monotonicMs);

    for (let i = 1; i < buffer.length; i++) {
      const diff = Math.abs(targetMs - buffer[i].monotonicMs);
      if (diff < minDiff) {
        minDiff = diff;
        closest = buffer[i];
      }
    }

    return closest;
  }

  onFusedData(callback: (record: FusedRecord) => void): void {
    this.callbacks.push(callback);
  }
}
